// Which kind of screen this is (issue 1179): a tablet or a desktop, read from the viewport's size and the primary
// pointer — `(pointer: coarse)`, a finger — and never from the user agent. The class keys the person's stored layout
// (`/api/me/layout?device=`), so the laptop's arrangement never lands on the tablet, and picks the stock arrangement
// a person with nothing saved for this class starts from. Both sides of the screen are read, so turning a tablet
// from landscape to portrait never changes its class (and never swaps the layout under the person's hands).
import { useEffect, useState } from 'react';
import type { DeviceClass } from '@dragons-table/vtt-shared';

/** A touch screen larger than this on its long side is a desktop with a touch screen, not a tablet (a 12.9-inch iPad is 1366). */
export const TABLET_LONG_SIDE_MAX = 1400;

export function deviceClassFor({ width, height, coarse }: { width: number; height: number; coarse: boolean }): DeviceClass {
  if (!coarse) return 'desktop';
  // A phone keeps the tablet's stored arrangement; it shows it in the phone layout (`phoneLayoutFor`, issue 1242).
  return Math.max(width, height) <= TABLET_LONG_SIDE_MAX ? 'tablet' : 'desktop';
}

/** A screen narrower than this on its short side is a phone (decision #1442): the smallest tablets are 744 px across. */
export const PHONE_SHORT_SIDE_MAX = 600;

/** Is this a phone-sized screen, whichever way it is held? */
export function phoneScreen({ width, height }: { width: number; height: number }): boolean {
  return Math.min(width, height) < PHONE_SHORT_SIDE_MAX;
}

/** A window this narrow under a mouse gets the phone layout (issue 1242); a wider one is a desktop's, however short. */
export const PHONE_LAYOUT_NARROW_MAX = 480;

/**
 * The phone layout (issue 1242): the board alone on the screen and the side bars opened on demand. A finger on a
 * phone-sized screen, either way up, or a mouse's window narrower than a phone's width. A short wide window under a
 * mouse stays the desktop's.
 */
export function phoneLayoutFor({ width, height, coarse }: { width: number; height: number; coarse: boolean }): boolean {
  return coarse ? phoneScreen({ width, height }) : width < PHONE_LAYOUT_NARROW_MAX;
}

/** A reading of the window, taken again on every resize. */
function useWindowReading(read: () => boolean): boolean {
  const [value, setValue] = useState(read);
  useEffect(() => {
    const on = () => setValue(read());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return value;
}

/** Is this window in the phone layout now? Read again on a resize, so a window narrowed past the line changes at once. */
export function usePhoneLayout(): boolean {
  return useWindowReading(() => typeof window !== 'undefined' && phoneLayoutFor({ width: window.innerWidth, height: window.innerHeight, coarse: coarsePointer() }));
}

/** Is this a phone-sized screen now? Read again on a resize, so a window widened past a phone's clears what was a phone's (issue 1242). */
export function usePhoneScreen(): boolean {
  return useWindowReading(() => typeof window !== 'undefined' && phoneScreen({ width: window.innerWidth, height: window.innerHeight }));
}

/** Is the primary pointer a finger? */
export function coarsePointer(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
}

/** This screen's class, read once when the table opens. */
export function currentDeviceClass(): DeviceClass {
  if (typeof window === 'undefined') return 'desktop';
  return deviceClassFor({ width: window.innerWidth, height: window.innerHeight, coarse: coarsePointer() });
}

/** The word for a press in a hint: "Tap" under a finger, "Click" under a mouse. */
export function pressWord(): 'Tap' | 'Click' {
  return coarsePointer() ? 'Tap' : 'Click';
}
